import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';

interface AccountTypeCardProps {
  userType: 'regular' | 'law_enforcement';
  lawEnforcementProfile?: any;
  isLoadingProfile: boolean;
  onUpgrade: () => void;
}

export const AccountTypeCard: React.FC<AccountTypeCardProps> = ({
  userType,
  lawEnforcementProfile,
  isLoadingProfile,
  onUpgrade
}) => {
  const isLawEnforcement = userType === 'law_enforcement';
  
  const getVerificationStatus = () => {
    if (!isLawEnforcement || !lawEnforcementProfile) return null;
    
    const status = lawEnforcementProfile.verificationStatus;
    if (status === 'approved' || status === 'auto_approved') {
      return { variant: 'success' as const, text: 'Verified' };
    } else if (status === 'rejected') {
      return { variant: 'error' as const, text: 'Rejected' };
    } else if (status === 'pending') {
      return { variant: 'warning' as const, text: 'Pending Review' };
    }
    return { variant: 'secondary' as const, text: 'Not Applied' };
  };

  const verificationStatus = getVerificationStatus();

  return (
    <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-indigo-200 dark:border-indigo-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isLawEnforcement 
              ? 'bg-indigo-600 dark:bg-indigo-600 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            {isLawEnforcement ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            )}
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isLawEnforcement ? 'Law Enforcement Account' : 'Regular Account'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {isLawEnforcement 
                ? 'Enhanced features for verified law enforcement professionals'
                : 'Standard account with basic search capabilities'
              }
            </p>
            {isLawEnforcement && verificationStatus && (
              <div className="mt-2">
                <Badge variant={verificationStatus.variant} size="sm">
                  {verificationStatus.text}
                </Badge>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {isLawEnforcement ? (
            <div className="text-right">
              <p className="text-sm text-gray-500 dark:text-gray-400">Account Status</p>
              <Badge variant="success" size="sm">
                Active
              </Badge>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onUpgrade}
              disabled={isLoadingProfile}
              className="min-h-[44px] touch-manipulation"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              }
            >
              {isLoadingProfile ? 'Upgrading...' : 'Upgrade to Law Enforcement'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
